import React, { useState, useEffect } from 'react';
import { Search, Check, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';


const DealManager = ({ dealData, onChange, allProducts }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Load full product details if ID exists
    useEffect(() => {
        if (dealData?.productId && !selectedProduct) {
            const found = allProducts.find(p => p.id === dealData.productId);
            if (found) setSelectedProduct(found);
        }
    }, [dealData, allProducts]);

    const handleSearch = (val) => {
        setSearchTerm(val);
        if (!val || val.length < 1) {
            setSearchResults([]);
            return;
        }

        const lower = val.toLowerCase();
        const results = allProducts
            .filter(p => p.name && p.name.toLowerCase().includes(lower))
            .slice(0, 10);

        setSearchResults(results);
    };

    const selectProduct = (p) => {
        setSelectedProduct(p);
        onChange('productId', p.id);
        // Clean search
        setSearchTerm("");
        setSearchResults([]);
    };

    const handleChange = (field, value) => {
        onChange(field, value);
    };

    return (
        <div className="space-y-8">

            {/* 1. Product Selection */}
            <div className="bg-white p-6 rounded-xl border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs">1</span>
                    {t('admin.homepage.deal.selectProduct')}
                </h3>

                {!selectedProduct ? (
                    <div className="relative">
                        <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder={t('admin.homepage.featured.searchPlaceholder')}
                            className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-600"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {searchResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                {searchResults.map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => selectProduct(p)}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b last:border-0"
                                    >
                                        <img src={p.image} className="w-10 h-10 object-contain" alt="" />
                                        <div>
                                            <div className="font-bold text-sm">{p.name}</div>
                                            <div className="text-xs text-gray-500">{p.price} ₺</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
                        <img src={selectedProduct.image} className="w-16 h-16 object-contain bg-white rounded-lg p-1" alt="" />
                        <div>
                            <h4 className="font-bold text-gray-800">{selectedProduct.name}</h4>
                            <p className="text-sm text-green-700 font-semibold">{selectedProduct.price} ₺</p>
                        </div>
                        <button
                            onClick={() => setSelectedProduct(null)}
                            className="ml-auto text-sm text-gray-500 hover:text-red-500 underline"
                        >
                            {t('admin.actions.change')}
                        </button>
                    </div>
                )}
            </div>

            {/* 2. Configuration */}
            <div className="bg-white p-6 rounded-xl border border-gray-100">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs">2</span>
                    {t('admin.homepage.deal.configuration')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Discount Percentage</label>
                        <div className="relative">
                            <input
                                type="number"
                                min="0" max="100"
                                className="w-full p-3 border rounded-xl font-bold text-lg"
                                value={dealData.discountPercent || ""}
                                onChange={e => handleChange('discountPercent', Number(e.target.value))}
                                placeholder="%"
                            />
                            <span className="absolute right-4 top-4 font-bold text-gray-400">%</span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">End Date & Time</label>
                        <div className="relative">
                            <Clock className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
                            <input
                                type="datetime-local"
                                className="w-full pl-10 pr-4 py-3 border rounded-xl"
                                value={dealData.endAt || ""}
                                onChange={e => handleChange('endAt', e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Stock Total</label>
                        <input
                            type="number"
                            className="w-full p-3 border rounded-xl"
                            value={dealData.stockTotal || ""}
                            onChange={e => handleChange('stockTotal', Number(e.target.value))}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Stock Left</label>
                        <input
                            type="number"
                            className="w-full p-3 border rounded-xl"
                            value={dealData.stockLeft || ""}
                            onChange={e => handleChange('stockLeft', Number(e.target.value))}
                        />
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <div className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${dealData.isActive ? 'bg-green-600 border-green-600' : 'bg-white border-gray-300'}`}>
                            {dealData.isActive && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <input
                            type="checkbox"
                            className="hidden"
                            checked={dealData.isActive}
                            onChange={e => handleChange('isActive', e.target.checked)}
                        />
                        <span className="font-bold text-gray-800">Activate Deal of the Day</span>
                    </label>
                </div>
            </div>

        </div>
    );
};

export default DealManager;
